import React, { useState, useCallback } from 'react';
import { Container } from 'react-bootstrap';
import Common from '../../shared/Common';
import type { IIncomeCategory } from '../../../interfaces/IIncomeCategory';
import type { ColumnDef } from '@tanstack/react-table';
import AddIncomeCategoryModal from './AddIncomeCategoryModal';
import IncomeCategoriesTable from './IncomeCategoriesTable';
import type { IIncomeCategoryData } from '../../../interfaces/IIncomeCategoryData';

import './style.css';

const IncomeCategories = () => {
  const [incomeCategories, setIncomeCategories] = useState<IIncomeCategoryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [removeButtonDisabled, setRemoveButtonDisabled] = useState(true);
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
  const [updatedIncomeCategories, setUpdatedIncomeCategories] = useState<IIncomeCategory[]>([]);
  const [selectedIncomeCategory, setSelectedIncomeCategory] = useState<IIncomeCategoryData | null>(null);

  const [fetchTrigger, setFetchTrigger] = useState(0);
  const fetchDataIdRef = React.useRef(0);

  const fetchData = useCallback(async () => {
    const fetchId = ++fetchDataIdRef.current;
    setLoading(true);
    const response = await Common.authorizedFetch('api/categories/getIncomeCategoriesData');
    const data = await response.json();
    if (data.success) {
      if (fetchId === fetchDataIdRef.current) {
        setIncomeCategories(data.result.incomeCategories);
        setLoading(false);
      }
      setRemoveButtonDisabled(true);
      setSaveButtonDisabled(true);
      setSelectedIncomeCategory(null);
      setUpdatedIncomeCategories([]);
    }
  }, []);

  const columns = React.useMemo<ColumnDef<IIncomeCategoryData>[]>(
    () => [
      {
        header: () => <span>Nazwa</span>,
        accessorFn: (row) => row.incomeCategory?.incomeCategoryName,
        id: 'incomeCategoryName',
      },
      {
        header: () => <span>Wykorzystano</span>,
        accessorFn: (row) => row.count,
        id: 'count',
      },
      {
        header: () => <span>Zapłacono</span>,
        accessorFn: (d) => (Math.round(d.totalRevenue * 100) / 100).toFixed(2),
        id: 'totalCost',
      },
    ],
    []
  );

  const updateIncomeCategoryName = (rowIndex: number, columnId: string, value: any) => {
    setIncomeCategories((prevState) => {
      const updated = prevState;
      if (
        updated[rowIndex].incomeCategory?.incomeCategoryId !== 1 &&
        updated[rowIndex].incomeCategory?.incomeCategoryName !== value
      ) {
        const ind = updatedIncomeCategories.findIndex(
          (l) => l.incomeCategoryId === updated[rowIndex].incomeCategory?.incomeCategoryId
        );
        if (ind === -1) {
          updatedIncomeCategories.push({
            ...updated[rowIndex].incomeCategory,
            incomeCategoryName: value,
          });
        } else {
          updatedIncomeCategories[ind].incomeCategoryName = value;
        }
        setUpdatedIncomeCategories(updatedIncomeCategories);
      }
      updated[rowIndex] = {
        ...prevState[rowIndex],
        [columnId]: value,
      };

      return updated;
    });
  };

  return (
    <Container className='mt-2'>
      <h2 className='mb-3' id='tabelLabel'>
        Kategorie przychodów
      </h2>
      {Common.Ui.showLoadingSpinnerFixed(showSpinner)}
      <IncomeCategoriesTable
        columns={columns}
        data={incomeCategories}
        loading={loading}
        setShowSpinner={setShowSpinner}
        fetchData={fetchData}
        fetchTrigger={fetchTrigger}
        setFetchTrigger={setFetchTrigger}
        updateIncomeCategoryName={updateIncomeCategoryName}
        updatedIncomeCategories={updatedIncomeCategories}
        setShowModal={setShowModal}
        saveButtonDisabled={saveButtonDisabled}
        removeButtonDisabled={removeButtonDisabled}
        setSaveButtonDisabled={setSaveButtonDisabled}
        setRemoveButtonDisabled={setRemoveButtonDisabled}
        selectedIncomeCategory={selectedIncomeCategory}
        setSelectedIncomeCategory={setSelectedIncomeCategory}
      />
      <AddIncomeCategoryModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
        }}
        onSave={() => {
          setFetchTrigger(fetchTrigger + 1);
        }}
      />
    </Container>
  );
};

export default IncomeCategories;
